import * as React from 'react'
import Head from 'next/head'
import { AppProps } from 'next/app'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { CacheProvider, EmotionCache } from '@emotion/react'
import themeConfig from '../src/theme'
import createEmotionCache from '../src/createEmotionCache'
import '../src/global.css'
import ConfirmationDialogContextProvider from '@/contexts/ConfirmationDialogContext'
import CookieContextProvider from '@/contexts/CookieContext'
import ToastsContextProvider from '@/contexts/ToastsContext'
import { useRouter } from 'next/router'
import LanguageContextProvider from '@/contexts/LanguageContext'
import UserContextProvider from '@/contexts/UserContext'
import PostHogPageView from '@/Components/FdComponents/PostHogPageView'
import usePostHog from '@/hooks/usePostHog'
import { PostHogProvider } from 'posthog-js/react'

const clientSideEmotionCache = createEmotionCache()

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache
}

export default function MyApp(props: MyAppProps) {
  const posthog = usePostHog()
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props
  const router = useRouter()
  React.useEffect(() => {
    const path = (/#!(\/.*)$/.exec(router.asPath) || [])[1]
    if (path) {
      router.replace(path)
    }
  }, [router.asPath])

  React.useEffect(() => {
    const disableContextMenu = (e: MouseEvent) => e.preventDefault()
    document.addEventListener('contextmenu', disableContextMenu)
    return () => document.removeEventListener('contextmenu', disableContextMenu)
  }, [])

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>fielddrive leads</title>
        <meta name='viewport' content='initial-scale=1, width=device-width' />
      </Head>
      <ThemeProvider theme={themeConfig}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <PostHogProvider client={posthog}>
          <LanguageContextProvider>
            <UserContextProvider>
              <CookieContextProvider>
                <ConfirmationDialogContextProvider>
                  <ToastsContextProvider>
                    <Component {...pageProps} />
                    <PostHogPageView />
                  </ToastsContextProvider>
                </ConfirmationDialogContextProvider>
              </CookieContextProvider>
            </UserContextProvider>
          </LanguageContextProvider>
        </PostHogProvider>
      </ThemeProvider>
    </CacheProvider>
  )
}
